import { createContext, useContext, useMemo, useCallback, useEffect, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AuthService } from '@/services/core/auth'
import { NetworksApi, Network } from '@/services/networks/networksApi'
import { getUserNetwork, setUserNetwork } from '@/services/core/context'
import { useCurrentUser } from './UserContext'
import { configureRollbarPerson } from '@/services/core/rollbar'

interface NetworkContextValue {
  networks: Network[]
  currentNetwork: Network | null
  loading: boolean
  /** Sets the primary network and reloads, optionally landing on `nextPath`. */
  switchNetwork: (tagId: string, nextPath?: string) => void
  /**
   * Stable identity of the network scope. Put this in the queryKey of a list
   * so its cache entry is per-network.
   */
  scopeKey: string
  /**
   * False until the network list has resolved. `scopeKey` is not final before
   * then, so gate an advertising list's `enabled` on this to avoid fetching it
   * once under a placeholder key and again under the real one.
   */
  scopeReady: boolean
}

const NetworkContext = createContext<NetworkContextValue>({
  networks: [],
  currentNetwork: null,
  loading: false,
  switchNetwork: () => {},
  scopeKey: '',
  scopeReady: false,
})

export const NETWORK_STORAGE_KEY = 'adnuntius.network'
const STORAGE_KEY = NETWORK_STORAGE_KEY

export function NetworkProvider({ children }: { children: ReactNode }) {
  const user = useCurrentUser()
  const authenticated = !!user || AuthService.isAuthenticated()

  const { data: allNetworks = [], isLoading: networksLoading } = useQuery({
    queryKey: ['networks'],
    queryFn: NetworksApi.getAll,
    enabled: authenticated,
    staleTime: Infinity,
  })

  const networks = user
    ? allNetworks.filter((n) => !!user.permissionsByNetwork[n.tagId])
    : allNetworks

  const savedTagId = localStorage.getItem(STORAGE_KEY)
  const userTagId = user ? getUserNetwork(user.id) : null
  const matchedNetwork =
    networks.find((n) => n.tagId === savedTagId) ??
    networks.find((n) => n.tagId === userTagId) ??
    networks[0] ??
    null

  // Persist the resolved network — covers first-time load and post-masquerade
  // where the previously saved tagId is no longer accessible to the current user
  if (matchedNetwork && matchedNetwork.tagId !== savedTagId) {
    localStorage.setItem(STORAGE_KEY, matchedNetwork.tagId)
  }
  if (user && matchedNetwork) setUserNetwork(user.id, matchedNetwork.tagId)

  // Fetch full network details for the current network
  const currentTagId = matchedNetwork?.tagId
  const { data: currentNetwork = null, isLoading: detailLoading } = useQuery({
    queryKey: ['network', currentTagId],
    queryFn: () => NetworksApi.getByTagId(currentTagId!),
    enabled: authenticated && !!currentTagId,
    staleTime: Infinity,
  })

  useEffect(() => {
    configureRollbarPerson(user, currentNetwork)
  }, [user, currentNetwork])

  const switchNetwork = useCallback((tagId: string, nextPath?: string) => {
    localStorage.setItem(STORAGE_KEY, tagId)
    // A full load is what makes every network-scoped query re-resolve; assigning
    // the destination reloads and navigates in one step.
    if (nextPath) {
      window.location.assign(nextPath)
    } else {
      window.location.reload()
    }
  }, [])

  const value = useMemo<NetworkContextValue>(
    () => ({
      networks,
      currentNetwork,
      loading: networksLoading || detailLoading,
      switchNetwork,
      scopeKey: currentTagId ?? '',
      scopeReady: authenticated && !networksLoading,
    }),
    [
      networks,
      currentNetwork,
      networksLoading,
      detailLoading,
      switchNetwork,
      currentTagId,
      authenticated,
    ],
  )

  return <NetworkContext.Provider value={value}>{children}</NetworkContext.Provider>
}

export function useNetwork() {
  return useContext(NetworkContext)
}
